/**
 * Sample data set. Kept apart from real data:
 *  - every record has sample: true and an id that starts with "sample-"
 *  - it is only added when the person asks (first-run choice or Settings)
 *  - "remove sample data" removes only these records and never a record the person created
 */
import { planImport, applyMerge } from "../domain/merge";
import type { Action, Category, Library, Workspace } from "../domain/types";

const d = (m: number, dd: number) => new Date(Date.UTC(2026, m - 1, dd, 2, 30)).toISOString(); // 09:30 in Thailand
type When = [number, number];

export function buildSampleLibrary(): Library {
  const workspaces: Workspace[] = [], categories: Category[] = [], actions: Action[] = [];
  let wsPos = 0;
  const W = (key: string, name: string, icon: string, c: When, u?: When) => {
    const ws: Workspace = { id: `sample-ws-${key}`, name, icon, position: wsPos++, sample: true, createdAt: d(...c), updatedAt: d(...(u || c)) };
    workspaces.push(ws);
    let catPos = 0;
    const C = (ckey: string, cname: string, cicon: string, glow: string, cc: When, cu?: When) => {
      const cat: Category = { id: `sample-cat-${key}-${ckey}`, workspaceId: ws.id, name: cname, icon: cicon, glow, layout: "standard", position: catPos++, sample: true, createdAt: d(...cc), updatedAt: d(...(cu || cc)) };
      categories.push(cat);
      let aPos = 0;
      const A = (type: "link" | "memo") => (akey: string, aname: string, aicon: string, color: string, body: string, ac: When, au?: When) => {
        actions.push({ id: `sample-act-${key}-${ckey}-${akey}`, categoryId: cat.id, type, name: aname, icon: aicon, color,
          url: type === "link" ? body : "", text: type === "memo" ? body : "", position: aPos++, sample: true, createdAt: d(...ac), updatedAt: d(...(au || ac)) });
      };
      return { L: A("link"), M: A("memo") };
    };
    return C;
  };

  let C = W("personal", "ส่วนตัว", "user", [9, 20]);
  let k = C("money", "การเงิน", "wallet", "amber", [9, 20], [10, 2]);
  k.M("saving", "เป้าออมเดือนนี้", "wallet", "amber", "[ข้อมูลตัวอย่าง]\nเป้าออม: 5,000 บาท\nโอนเข้าบัญชีออมทุกวันที่ 1\nทบทวนรายจ่ายทุกวันอาทิตย์", [9, 20], [10, 2]);
  k.L("bills", "ปฏิทินจ่ายบิล", "calendar", "sky", "https://calendar.google.com/", [9, 21]);
  k = C("health", "สุขภาพ", "heart", "rose", [9, 22]);
  k.M("dentist", "นัดหมอฟัน", "note", "rose", "[ข้อมูลตัวอย่าง]\nนัดขูดหินปูนครั้งถัดไป\nเตรียม: บัตรประชาชน, ประวัติแพ้ยา", [9, 22]);
  k.L("yoga", "วิดีโอโยคะ", "video", "rose", "https://www.youtube.com/", [9, 22]);
  k = C("learn", "เรียนรู้", "book", "sky", [9, 24], [10, 5]);
  k.L("wiki", "Wikipedia", "globe", "slate", "https://www.wikipedia.org/", [9, 24]);
  k.M("books", "หนังสือที่อยากอ่าน", "book", "sky", "[ข้อมูลตัวอย่าง]\n1. หนังสือการเงินส่วนบุคคล\n2. หนังสือออกแบบ UX\n3. นิยายเล่มใหม่", [9, 24], [10, 5]);

  C = W("office", "Office", "briefcase", [9, 18], [10, 8]);
  k = C("links", "ลิงก์สำคัญ", "link", "violet", [10, 1], [10, 8]);
  k.L("breakeven", "Sheet คำนวณ Breakeven", "sheet", "green", "", [10, 1], [10, 8]);
  k.L("drive", "Google Drive", "folder", "amber", "https://drive.google.com/", [10, 1]);
  k.L("gmail", "Gmail", "mail", "rose", "https://mail.google.com/", [10, 1]);
  k.M("closing", "ขั้นตอนปิดยอดสิ้นเดือน", "clipboard", "violet", "[ข้อมูลตัวอย่าง]\n1. ดึงยอดขายจากระบบ\n2. กระทบยอดกับใบแจ้งหนี้\n3. อัปเดต Sheet Breakeven\n4. ส่งสรุปให้หัวหน้าภายในวันที่ 3", [10, 1], [10, 6]);
  k = C("meeting", "ประชุม", "calendar", "sky", [9, 28], [10, 7]);
  k.L("meet", "Google Meet", "video", "green", "https://meet.google.com/", [9, 28]);
  k.M("agenda", "วาระประชุมประจำสัปดาห์", "note", "sky", "[ข้อมูลตัวอย่าง]\n- อัปเดตยอดขายสัปดาห์นี้\n- งานค้างของทีม\n- เรื่องอื่น ๆ", [9, 28], [10, 7]);
  k = C("docs", "เอกสาร", "folder", "amber", [9, 25]);
  k.M("reply", "แม่แบบอีเมลตอบลูกค้า", "mail", "amber", "[ข้อมูลตัวอย่าง]\nเรียน คุณลูกค้า\n\nขอบคุณที่ติดต่อเข้ามา ทางเราได้รับเรื่องเรียบร้อยแล้ว และจะแจ้งความคืบหน้าภายใน 2 วันทำการ\n\nขอแสดงความนับถือ", [9, 25]);
  C("reports", "รายงาน", "chart", "green", [9, 26]);
  k = C("team", "ทีม", "users", "pink", [9, 27]);
  k.M("contact", "ช่องทางติดต่อทีม", "chat", "pink", "[ข้อมูลตัวอย่าง]\nคุยงานด่วน: กลุ่มแชตทีม\nส่งไฟล์: โฟลเดอร์ทีมใน Drive\nลางาน: แจ้งหัวหน้าล่วงหน้า 1 วัน", [9, 27]);
  k = C("tools", "เครื่องมือ", "code", "sky", [9, 29]);
  k.L("github", "GitHub", "code", "slate", "https://github.com/", [9, 29]);

  C = W("other", "อื่น ๆ", "sparkles", [9, 30]);
  k = C("ideas", "ไอเดีย", "bulb", "amber", [9, 30]);
  k.M("app", "ไอเดียแอป", "bulb", "amber", "[ข้อมูลตัวอย่าง]\nแอปเก็บลิงก์แบบ Stream Deck\nแตะครั้งเดียวเปิดงานที่ใช้บ่อย", [9, 30]);
  k = C("travel", "ท่องเที่ยว", "pin", "green", [9, 30]);
  k.L("maps", "Google Maps", "pin", "green", "https://maps.google.com/", [9, 30]);
  k = C("shopping", "ช้อปปิ้ง", "cart", "pink", [10, 3]);
  k.M("list", "ของที่ต้องซื้อ", "cart", "pink", "[ข้อมูลตัวอย่าง]\n- ถ่าน AA\n- สายชาร์จ USB-C\n- กาแฟ", [10, 3]);

  return { workspaces, categories, actions, settings: { activeWorkspaceId: "sample-ws-office", openWith: "default" } };
}

/** Adds any sample record not already present. Existing records (sample or not) are left untouched. */
export function addSampleData(lib: Library): Library {
  const sample = buildSampleLibrary();
  const out = applyMerge(lib, planImport(lib, sample), "keep-local");
  if (!lib.workspaces.length) out.settings.activeWorkspaceId = sample.settings.activeWorkspaceId;
  return out;
}

/**
 * Removes sample records only. A sample category or workspace that now holds the person's own records is kept
 * (and stops being marked as sample) so nothing of theirs disappears.
 */
export function removeSampleData(lib: Library): { library: Library; removed: number; kept: number } {
  const before = lib.workspaces.length + lib.categories.length + lib.actions.length;
  let kept = 0;
  const actions = lib.actions.filter(a => !a.sample);
  const categories = lib.categories.filter(c => {
    if (!c.sample) return true;
    if (actions.some(a => a.categoryId === c.id)) { kept++; return true; }
    return false;
  }).map(c => (c.sample && actions.some(a => a.categoryId === c.id) ? withoutSample(c) : c));
  const workspaces = lib.workspaces.filter(w => {
    if (!w.sample) return true;
    if (categories.some(c => c.workspaceId === w.id)) { kept++; return true; }
    return false;
  }).map(w => (w.sample ? withoutSample(w) : w));
  const out: Library = { workspaces, categories, actions, settings: { ...lib.settings } };
  if (!workspaces.some(w => w.id === out.settings.activeWorkspaceId)) out.settings.activeWorkspaceId = workspaces[0]?.id ?? null;
  return { library: out, removed: before - (workspaces.length + categories.length + actions.length), kept };
}
function withoutSample<T extends { sample?: boolean }>(r: T): T { const { sample: _s, ...rest } = r; return rest as T; }
